import type { QueryClient } from '@tanstack/react-query';

import type { UiWorkoutExercise } from '../../schemas/workouts.schema';
import type { UiExercise } from '../../schemas/exercises.schema';

/**
 * Refreshes a cached workout's exercise entry after the underlying exercise
 * was edited. UiExercise is a different entity than UiWorkoutExercise — a
 * whole-object spread would clobber per-workout state (the instance `comment`,
 * the stable `uniqueId` which the mapper regenerates on every response) and
 * leak exercise-library-only fields into the workout shape. Copy only the
 * display fields an exercise edit can actually change, keeping existing values
 * when the response omits them.
 */
export function applyExerciseEdit(
  workoutExercise: UiWorkoutExercise,
  exercise: UiExercise,
): UiWorkoutExercise {
  return {
    ...workoutExercise,
    coverSignedUrl: exercise.coverSignedUrl ?? workoutExercise.coverSignedUrl,
    coverType: exercise.coverType ?? workoutExercise.coverType,
    mediaSignedUrl: exercise.mediaSignedUrl ?? workoutExercise.mediaSignedUrl,
    mediaType: exercise.mediaType ?? workoutExercise.mediaType,
    translations: exercise.translations ?? workoutExercise.translations,
  };
}

/**
 * Refreshes every cached workout detail containing the edited exercise. The
 * real fetchWorkoutById key ends with the caller's userUnitSettings object, so
 * an exact-key setQueryData built without it never matches — React Query then
 * hands the updater `undefined`, the updater returns it, and nothing is
 * stored: the old "refresh" was a silent no-op. Match by key PREFIX instead.
 * `workoutDetailDef` is the query-key-factory `_def` for fetchWorkoutById,
 * passed in by the caller (this module stays import-light so the standalone
 * node test can load it).
 */
export function applyExerciseEditToWorkoutCaches(
  queryClient: QueryClient,
  {
    exercise,
    organizationId,
    workoutDetailDef,
    workoutId,
  }: {
    workoutDetailDef: readonly string[];
    organizationId?: string;
    exercise: UiExercise;
    workoutId: string;
  },
) {
  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: [...workoutDetailDef, organizationId, workoutId],
    },
    (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        groups: oldData.groups?.map((group: any) => ({
          ...group,
          exercises: group.exercises?.map((we: UiWorkoutExercise) =>
            we.id === exercise.id ? applyExerciseEdit(we, exercise) : we,
          ),
        })),
      };
    },
  );
}
